import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Space, Alert, Button, Result } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import loginImage from '../static/ColourBrandPrimary2x.png';
import '../style/Login.css';

const { Title, Text } = Typography;

const VerifyRegistration: React.FC = () => {
    const { token, userId } = useParams<{ token: string; userId: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading');
    const [message, setMessage] = useState<string>('');
    const [userInfo, setUserInfo] = useState<any>(null);

    useEffect(() => {
        const verifyRegistration = async () => {
            if (!token) {
                setVerificationStatus('error');
                setMessage('Invalid verification link');
                setLoading(false);
                return;
            }

            try {
                const verificationUrl = userId
                    ? `${ENDPOINTS.VERIFY_REGISTRATION(token)}/${encodeURIComponent(userId)}`
                    : ENDPOINTS.VERIFY_REGISTRATION(token);
                const response = await axios.get(verificationUrl);
                setVerificationStatus('success');
                setMessage(response.data.message);
                setUserInfo(response.data.user);
                
                // Auto-redirect to login after 5 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 5000);
                
            } catch (error: any) {
                if (error.response?.status === 403) {
                    setVerificationStatus('expired');
                    setMessage(error.response.data.message || 'Verification link has expired');
                } else {
                    setVerificationStatus('error');
                    setMessage(error.response?.data?.message || 'Verification failed');
                }
            } finally {
                setLoading(false);
            }
        };

        verifyRegistration();
        
        document.body.classList.add("login-page");
        return () => {
            document.body.classList.remove("login-page");
        };
    }, [token, userId, navigate]);

    const handleGoToLogin = () => {
        navigate('/login');
    };

    const getResultIcon = () => {
        switch (verificationStatus) {
            case 'success':
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'expired':
                return <ClockCircleOutlined style={{ color: '#faad14' }} />;
            case 'error':
                return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
            default:
                return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
        }
    };

    const getResultStatus = () => {
        switch (verificationStatus) {
            case 'success':
                return 'success';
            case 'expired':
                return 'warning';
            case 'error':
                return 'error';
            default:
                return 'info';
        }
    };

    const getResultTitle = () => {
        switch (verificationStatus) {
            case 'success':
                return 'Email Verified Successfully!';
            case 'expired':
                return 'Verification Link Expired';
            case 'error':
                return 'Verification Failed';
            default:
                return 'Verifying Your Email...';
        }
    };

    return (
        <div className="login-view">
            <div className="login-container" style={{ maxWidth: '600px', margin: '0 auto' }}>
                <div className="image-container">
                    <img src={loginImage} alt="E-Nano" className="login-image" />
                </div>
                <div className="form-container">
                    <Card style={{ width: '100%' }}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <div style={{ textAlign: 'center' }}>
                                <Title level={2}>
                                    Email Verification
                                </Title>
                                <Text type="secondary">
                                    Verifying your OSCAR user account
                                </Text>
                            </div>

                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <ClockCircleOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                                    <div style={{ marginTop: 16 }}>
                                        <Title level={4}>Verifying your email address...</Title>
                                        <Text type="secondary">Please wait while we process your verification.</Text>
                                    </div>
                                </div>
                            ) : (
                                <Result
                                    icon={getResultIcon()}
                                    status={getResultStatus()}
                                    title={getResultTitle()}
                                    subTitle={message}
                                    extra={
                                        <Space direction="vertical" size="middle">
                                            {verificationStatus === 'success' && userInfo && (
                                                <Alert
                                                    message="Account Details"
                                                    description={
                                                        <div>
                                                            <p><strong>Email:</strong> {userInfo.email}</p>
                                                            <p>Company setup will be available after you log in.</p>
                                                        </div>
                                                    }
                                                    type="success"
                                                    showIcon
                                                />
                                            )}
                                            
                                            {verificationStatus === 'success' && (
                                                <Alert
                                                    message="Auto-redirect"
                                                    description="You will be automatically redirected to login in a few seconds..."
                                                    type="info"
                                                />
                                            )}

                                            <Button 
                                                type="primary" 
                                                onClick={handleGoToLogin}
                                                size="large"
                                            >
                                                {verificationStatus === 'success' ? 'Login Now' : 'Back to Login'}
                                            </Button>

                                            {(verificationStatus === 'expired' || verificationStatus === 'error') && (
                                                <Button 
                                                    onClick={() => navigate('/login')}
                                                    size="large"
                                                >
                                                    Try Registration Again
                                                </Button>
                                            )}
                                        </Space>
                                    }
                                />
                            )}
                        </Space>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default VerifyRegistration;
